import { baseStore, constants, coreStore, dispatcher, services } from '@ambita/ambita-components-core';
import * as landRegistryConstants from '../constants/landRegistry';
import * as statesConstants from '../constants/componentStates';
import { LandRegistryLookupParts, LandRegistryLookupState } from '../typings/LandRegistryLookup';

const { filters, util } = services;
const {
  actions: landRegistryActions,
  events: landRegistryEvents,
  states: landRegistryStates,
  types: landRegistryTypes
} = landRegistryConstants;

export interface LandRegistryType {
  active: { data?: any; status?: Number; error?: Object; tagStatus?: Number };
  historical: { data?: any; status?: Number; error?: Object; tagStatus?: Number };
  active_and_historical: { data?: any; status?: Number; error?: Object; tagStatus?: Number };
}

export interface LandRegistryState {
  ENCUMBRANCES: LandRegistryType;
  EASEMENTS: LandRegistryType;
  TITLES: LandRegistryType;
  LANDDATA: LandRegistryType;
  MONETARY_ENCUMBRANCES: LandRegistryType;
  PRIVILEGES: LandRegistryType;
  right: {
    data?: any;
    status?: Number;
    error?: Object;
  };
  lastQuery: {
    key: string;
    eventState: string
  };
  orderReference?: string;
}

export interface LandRegistryAction extends baseStore.StoreAction {
  component?: string;
  landRegistryPartName?: LandRegistryLookupParts;
  landRegistryPartState?: LandRegistryLookupState;
  response?: any;
  cadastreKey?: string;
  knr?: string;
  right?: any;
  rightKey?: string;
  errorType?: any;
  orderReference?: string;
}

const initialLandRegistryType = {
  active: { status: statesConstants.INITIAL, tagStatus: statesConstants.INITIAL },
  historical: { status: statesConstants.INITIAL, tagStatus: statesConstants.INITIAL },
  active_and_historical: { status: statesConstants.INITIAL, tagStatus: statesConstants.INITIAL }
};

const updateState = (state: LandRegistryState): LandRegistryState => {
  return util.assign({}, state);
};

const getCurrentTime = (): string => {
  return filters.date(Date.now(), 'dd.MM.yyyy kl. HH:mm');
};

const setOrderReference = (state: LandRegistryState, orderReference: string): LandRegistryState => {
  return {
    ...state,
    orderReference
  };
};

const updateLandRegistryTagsStatus = (
  state: LandRegistryState,
  landRegistryPartName: LandRegistryLookupParts,
  landRegistryPartState: LandRegistryLookupState,
  status: Number
): LandRegistryState => {
  state[landRegistryPartName][landRegistryPartState].tagStatus = status;
  return updateState(state);
};

const setLoadingStatus = (state: LandRegistryState, action: LandRegistryAction): LandRegistryState => {
  if (action.component === landRegistryTypes.RIGHT) {
    return {
      ...state,
      right: {
        status: statesConstants.LOADING,
        data: null,
        error: null
      }
    };
  } else {
    state[action.landRegistryPartName][action.landRegistryPartState].status = statesConstants.LOADING;
    state[action.landRegistryPartName][action.landRegistryPartState].data = null;
    state[action.landRegistryPartName][action.landRegistryPartState].error = null;
    return updateState(state);
  }
};

const storeRight = (state: LandRegistryState, right: any, rightKey: any): LandRegistryState => {
  const rightData = {
    ...right,
    searchKey: rightKey
  };

  return {
    ...state,
    right: {
      data: rightData,
      status: statesConstants.DONE_SUCCESS
    }
  };
};

const storeRightError = (state: LandRegistryState, errorType: Object): LandRegistryState => {
  return {
    ...state,
    right: {
      data: {},
      status: statesConstants.DONE_FAILED,
      error: errorType
    }
  };
};

const store = (
  state: LandRegistryState,
  landRegistryPartName: LandRegistryLookupParts,
  data: any,
  searchKey: any,
  knr: any,
  landRegistryPartState: LandRegistryLookupState
): LandRegistryState => {
  state[landRegistryPartName][landRegistryPartState].data = {
    ...data,
    timeFetched: getCurrentTime(),
    searchKey,
    knr
  };
  state[landRegistryPartName][landRegistryPartState].status = statesConstants.DONE_SUCCESS;
  state.lastQuery = {
    ...state.lastQuery,
    key: searchKey,
    eventState: landRegistryPartState
  };
  return updateState(state);
};

const storeError = (
  state: LandRegistryState,
  landRegistryPartName: LandRegistryLookupParts,
  landRegistryPartState: LandRegistryLookupState,
  errorType: any
): LandRegistryState => {
  state[landRegistryPartName][landRegistryPartState].data = {};
  state[landRegistryPartName][landRegistryPartState].status = statesConstants.DONE_FAILED;
  state[landRegistryPartName][landRegistryPartState].error = errorType;
  return updateState(state);
};

const clearLandRegistryState = (state: LandRegistryState): LandRegistryState => {
  return {
    ...state,
    ENCUMBRANCES: util.clone(initialLandRegistryType),
    EASEMENTS: util.clone(initialLandRegistryType),
    TITLES: util.clone(initialLandRegistryType),
    LANDDATA: util.clone(initialLandRegistryType),
    MONETARY_ENCUMBRANCES: util.clone(initialLandRegistryType),
    PRIVILEGES: util.clone(initialLandRegistryType),
    lastQuery: {
      key: '',
      eventState: 'active'
    }
  };
};

const areAllLandRegistryTagsFinishedLoading = (state: LandRegistryState): boolean => {
  const allLandRegistryTypes = Object.keys(landRegistryTypes)
    .map(key => landRegistryTypes[key])
    .filter(element => element !== landRegistryTypes.RIGHT);
  const allLandRegistryStates = Object.keys(landRegistryStates)
    .map(key => landRegistryStates[key]);

  return allLandRegistryTypes.every(type => {
    const landRegistryType = state[type];
    // For every land registry state, check that the land registry type is not loading
    return allLandRegistryStates.every(landRegistryState => {
      return landRegistryType[landRegistryState].tagStatus !== statesConstants.LOADING;
    });
  });
};

const stateReducer = (state: LandRegistryState, action: LandRegistryAction): LandRegistryState => {
  switch (action.actionType) {
    case landRegistryActions.STORE_LAND_REGISTRY_PART:
      return store(
        state,
        action.landRegistryPartName,
        action.response,
        action.cadastreKey,
        action.knr,
        action.landRegistryPartState
      );
    case landRegistryActions.STORE_LAND_REGISTRY_PART_ERROR:
      return storeError(state, action.landRegistryPartName, action.landRegistryPartState, action.errorType);
    case landRegistryActions.STORE_RIGHT:
      return storeRight(state, action.right, action.rightKey);
    case landRegistryActions.STORE_RIGHT_ERROR:
      return storeRightError(state, action.errorType);
    case landRegistryActions.SET_LOADING_STATUS:
      return setLoadingStatus(state, action);
    case landRegistryActions.LAND_REGISTRY_TAG_LOADING:
      return updateLandRegistryTagsStatus(
        state,
        action.landRegistryPartName,
        action.landRegistryPartState,
        statesConstants.LOADING
      );
    case landRegistryActions.LAND_REGISTRY_TAG_LOADING_COMPLETE:
      return updateLandRegistryTagsStatus(
        state,
        action.landRegistryPartName,
        action.landRegistryPartState,
        statesConstants.DONE_SUCCESS
      );
    case landRegistryActions.SET_ORDER_REFERENCE:
      return setOrderReference(state, action.orderReference);
    case constants.core.LOGOUT_ACTION:
      dispatcher.waitFor([coreStore.dispatchToken]);
      return clearLandRegistryState(state);
    default:
      return state;
  }
};

const eventCollector = (state: LandRegistryState, action: LandRegistryAction): baseStore.StoreEvent => {
  switch (action.actionType) {
    case landRegistryActions.CLEAR_STATE:
    case landRegistryActions.STORE_LAND_REGISTRY_PART:
      return {
        name: landRegistryEvents.CADASTRE_CHANGED
      };
    case landRegistryActions.LAND_REGISTRY_TAG_LOADING_COMPLETE:
      if (areAllLandRegistryTagsFinishedLoading(state)) {
        return {
          name: landRegistryEvents.LAND_REGISTRY_LOADING_COMPLETE
        };
      }
      return null;
    case landRegistryActions.PURCHASE_PANTEBOK:
      return {
        name: landRegistryEvents.PURCHASE_PANTEBOK,
        args: [action.rightKey]
      };
    case landRegistryActions.PURCHASED_PANTEBOK:
      return {
        name: landRegistryEvents.PURCHASED_PANTEBOK,
        args: [action.rightKey]
      };
    case landRegistryActions.PURCHASE_PANTEBOK_FAILED:
      return {
        name: landRegistryEvents.PURCHASE_PANTEBOK_FAILED,
        args: [action.rightKey]
      };
    case landRegistryActions.SET_ORDER_REFERENCE:
      return {
        name: landRegistryEvents.SET_ORDER_REFERENCE,
        args: [action.orderReference]
      };
    default:
      return null;
  }
};

const initialState: LandRegistryState = {
  ENCUMBRANCES: util.clone(initialLandRegistryType),
  EASEMENTS: util.clone(initialLandRegistryType),
  TITLES: util.clone(initialLandRegistryType),
  LANDDATA: util.clone(initialLandRegistryType),
  MONETARY_ENCUMBRANCES: util.clone(initialLandRegistryType),
  PRIVILEGES: util.clone(initialLandRegistryType),
  right: {},
  lastQuery: {
    key: '',
    eventState: 'active'
  }
};

class LandRegistryStore extends baseStore.Store<LandRegistryState> {
  getLandRegistryPart(
    landRegistryPartName: LandRegistryLookupParts,
    landRegistryPartState: LandRegistryLookupState
  ): any {
    return this.getState()[landRegistryPartName][landRegistryPartState];
  }

  getRight(): any {
    return this.getState().right;
  }

  areAllLandRegistryTagsFinishedLoading(): boolean {
    return areAllLandRegistryTagsFinishedLoading(this.getState());
  }
}

export default new LandRegistryStore(stateReducer, eventCollector, initialState);
